import { Button, FormControlLabel, Link, MenuItem, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormikProps } from 'formik';
import moment from 'moment';
import React, { useMemo, useRef, useState } from 'react';
import { useRecoilValueLoadable } from 'recoil';
import {
  FormikSelect,
  FormikSwitch,
  FormikTextField,
} from '../../../common/formik-material-ui';
import { Business } from '../../../lib/kixie-entities/tables/Business';
import { Cadence } from '../../../lib/kixie-entities/tables/Cadence';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
} from '../../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import InfoPanel from '../InfoPanel/InfoPanel';
import { ModalWithStyles } from '../modals/ModalWithStyles';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';
import { createHoursString, Scheduler } from './Scheduler';

const useStyles = makeStyles((theme: Theme) => ({
  editButton: {
    maxWidth: '155px',
    maxHeight: '50px',
    minWidth: '155px',
    minHeight: '50px',
  },
  closeIcon: {
    position: 'absolute',
    top: '10px',
    right: '20px',
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    width: '18vw',
  },
  smsField: {
    minHeight: '3em',
  },
  smsWidth: {
    width: '495px',
  },
  fieldInsideRow: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
    width: '110px',
  },
  timeframes: {
    marginTop: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  helpCopy: {
    maxWidth: '725px',
    fontSize: '15px',
  },
}));

export const dayNames = [
  'placeholder',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const cellsReset = Array.from({ length: 7 + 1 }, (i) =>
  Array.from({ length: 48 + 1 }, (j) => false)
);
export const tableLookup = Array.from({ length: 7 + 1 }, (i, dayIndex) =>
  Array.from({ length: 48 + 1 }, (j, timeIndex) => ({
    day: dayNames[dayIndex],
    time: {
      startTime: moment()
        .startOf('day')
        .add((timeIndex - 1) * 30, 'minutes')
        .format('HH:mm'),

      endTime: moment()
        .startOf('day')
        .add(timeIndex * 30 - (timeIndex === 48 ? 1 : 0), 'minutes') // last time will be 11:59 pm
        .format('HH:mm'),
    },
  }))
);

export function Settings(props: {
  formik: FormikProps<Cadence>;
  id: number | null;
}) {
  const classes = useStyles();
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const businessQueues = useRecoilValueLoadable(
    makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_QUEUES })
  );

  const smsSender = [
    { label: 'Use Agent', value: 'useAgent' },
    { label: 'Team SMS', value: 'teamSMS' },
  ];
  const queuesCached = useRef<any[]>([]);
  queuesCached.current = React.useMemo(
    () =>
      businessQueues.contents?.data
        ? [...(businessQueues.contents?.data?.result ?? [])].sort((a, b) =>
            a?.queuename
              ?.toLowerCase()
              ?.localeCompare(b?.queuename?.toLowerCase())
          )
        : queuesCached.current,
    [businessQueues]
  );

  const userLoadable = useRecoilValueLoadable(user);
  const userAtom = userLoadable.contents as IUserAtom;

  const businessSMSInboxes = useRecoilValueLoadable(
    makeAPIRequest({
      // stage: 'itn',
      method: 'get',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint:
        endpointEnum.BUSINESS_SMS_BY_BUSINESS + `/${userAtom.businessid ?? ''}`,
      payloadJsonString: JSON.stringify({
        businessId: userAtom.businessid ?? '',
      }),
    })
  );

  const [_, businessSMSNumbersLoadable] = makeApiRequestCached(
    {
      method: 'get',
      // stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint:
        endpointEnum.BUSINESS_SMS_BY_BUSINESS +
        `/${userAtom?.businessid}` +
        `/${props.formik.values.smsInboxId}`,
      payloadJsonString: JSON.stringify({
        apikey: userAtom?.apikey,
        businessid: userAtom?.businessid,
        businessId: userAtom?.businessid,
      }),
      waitForPayload: !props.formik.values.smsInboxId,
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result ?? cached.current
  );

  const [businessCached, businessLoadable] = makeApiRequestCached<
    Business | undefined
  >(
    {
      endpoint: `${endpointEnum.GET_BUSINESSES}/${userAtom.businessid}`,
      method: 'get',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.[0] ?? cached.current
  );

  const businessSMSArray = useMemo(
    () =>
      businessSMSInboxes.state === 'hasValue'
        ? [
            ...(businessSMSInboxes.contents?.response?.data?.data ?? []),
          ]?.sort((a, b) =>
            a?.name?.toLowerCase()?.localeCompare(b?.name?.toLowerCase())
          ) ?? []
        : [],
    [businessSMSInboxes]
  );

  const businessInboxNumbers =
    businessSMSNumbersLoadable?.contents?.response?.data?.data?.numbers || [];
  // create object that will use index as identifier
  // console.log(smsTemplateOptions, 'option sms templates');

  const schedulableHoursCached = useRef();
  const handleSetSchedulableHours = (hours: any) => {
    schedulableHoursCached.current = hours;
  };

  const cadenceHoursString = useMemo<string[]>(
    () =>
      createHoursString(
        props.formik.values.useBusinessCadenceHoursEnabled
          ? businessCached.current?.businessDetails?.businessHours
          : props.formik.values.cadenceHours
      ).split(','),
    [
      props.formik.values.useBusinessCadenceHoursEnabled,
      props.formik.values.cadenceHours,
      businessLoadable,
    ]
  );

  const targetHoursString = useMemo<string[]>(
    () =>
      createHoursString(
        props.formik.values.useBusinessTargetHoursEnabled
          ? businessCached.current?.businessDetails?.businessHours
          : props.formik.values.targetHours
      ).split(','),
    [
      props.formik.values.useBusinessTargetHoursEnabled,
      props.formik.values.targetHours,
      businessLoadable,
    ]
  );

  // state for business hours modal;
  const [showBusinessHours, setShowBusinessHours] = useState(false);
  const handleShowBusinessHours = () => setShowBusinessHours(true);
  const onHide = () => {
    setShowBusinessHours(false);
    setShowTargetHours(false);
  };
  // state for target hours modal
  const [showTargetHours, setShowTargetHours] = useState(false);
  const handleShowTargetHours = () => setShowTargetHours(true);

  function handleSubmit() {
    if (!createHoursString(schedulableHoursCached.current)) {
      enqueueSnackbarWithStyles({
        message: 'Please select a time range',
        alertProps: {
          severity: 'warning',
        },
      });
    } else {
      if (showBusinessHours) {
        props.formik.setFieldValue(
          'cadenceHours',
          schedulableHoursCached.current
        );
      } else {
        props.formik.setFieldValue(
          'targetHours',
          schedulableHoursCached.current
        );
      }
      onHide();
    }
  }

  // const durations = ['Daily', 'Hourly', 'Monthly', 'Yearly'];
  return (
    <>
      <div className="container">
        <InfoPanel>
          <div>
            Configure and add details to your Cadence on this tab. Set a name,
            toggle settings, and assign a Call Queue. To setup a Cadence, you'll
            need a Call Queue setup. Learn how to do that{' '}
            <Link
              href="https://support.kixie.com/hc/en-us/articles/360047302494-Setting-Up-Your-Call-Queue"
              underline="hover"
              target="_blank"
            >
              here
            </Link>
            . A Custom Webhook will be added to a new Cadence once saved.
          </div>
        </InfoPanel>
        {props.id && (
          <div className="row borderBottom">
            <div className="col-lg-8">
              <h5 className="fieldTitle">Unique Webhook</h5>
              <p className="fieldDescription">
                This is the webhook URL that will trigger events for your
                Cadence. Copy and use it in your automation system to enroll
                contacts into this Cadence based on specific actions or
                conditions.
              </p>
            </div>
            <div className="col-lg-4">
              <FormikTextField
                className="w-100"
                formik={props.formik}
                name="eventUrl"
                label="Unique Webhook"
                InputProps={{
                  readOnly: true,
                }}
                size="small"
                variant="outlined"
              />
            </div>
          </div>
        )}
        <div className="row borderBottom">
          <div className="col-lg-8">
            <h5 className="fieldTitle">Cadence Name</h5>
            <p className="fieldDescription">
              Enter a name for this Cadence. This name will help you identify it
              in your reports and settings. Make it descriptive enough to
              represent the workflow or purpose.
            </p>
          </div>
          <div className="col-lg-4">
            <FormikTextField
              formik={props.formik}
              fullWidth
              name="cadenceName"
              inputProps={{ maxLength: 45 }}
              label="Cadence Name"
              required
              size="small"
              variant="outlined"
            />
          </div>
        </div>
        <div className="row borderBottom">
          <div className="col-lg-8">
            <h5 className="fieldTitle">Call Queue</h5>
            <p className="fieldDescription">
              Select a Queue to handle the calls in this Cadence. Calls will be
              routed to this Queue based on the Cadence steps.
            </p>
          </div>
          <div className="col-lg-4">
            <FormikSelect
              name="queueId"
              formik={props.formik}
              fullWidth
              label="Call Queue"
              required
              size="small"
              variant="outlined"
            >
              {queuesCached.current.map((queue, index) => (
                <MenuItem key={index} value={queue.queueid}>
                  {queue.queuename}
                </MenuItem>
              )) ?? []}
            </FormikSelect>
          </div>
        </div>
        <div className="row borderBottom">
          <div className="col-lg-8">
            <h5 className="fieldTitle">Cadence Actions Owner</h5>
            <p className="fieldDescription">
              Determines the sender of calls and texts in this Cadence:
              Auto-Assign from Queue assigns at random from the Queue, Assign
              from Queue on Answer assigns to the first agent to answer the
              first call action. The agent chosen becomes the owner of the
              contact in your connected CRM.
            </p>
          </div>
          <div className="col-lg-4 mb-5">
            <FormikSelect
              name="assignContactOwner"
              formik={props.formik}
              label="Assign Cadence Actions Owner"
              size="small"
              variant="outlined"
            >
              <MenuItem value={'autoAssignFromQueue'}>
                Auto-Assign from Queue
              </MenuItem>
              <MenuItem value={'assignFromQueueOnAnswer'}>
                Assign from Queue on Answer
              </MenuItem>
            </FormikSelect>
            <FormControlLabel
              className="mb-0"
              control={
                <FormikSwitch
                  name="useContactOwnerEnabled"
                  formik={props.formik}
                  color="primary"
                  type="checkbox"
                />
              }
              label="Use CRM Contact Owner"
            />
            <br />
            <small className="fieldDescription">
              When ON, the CRM contact owner is the sender of calls/texts in
              this Cadence. Otherwise, the Cadence Actions Owner rules apply.
            </small>
          </div>
        </div>
        <div className="row borderBottom">
          <div className="col-lg-8">
            <h5 className="fieldTitle">SMS Sender</h5>
            <p className="fieldDescription">
              Choose the sender for SMS messages as part of the actions in this
              Cadence. Select either the Cadence Actions Owner from the previous
              step or a Team SMS inbox .
            </p>
          </div>
          <div className="col-lg-4">
            <FormikSelect
              name="smsSender"
              // className={classes.field}
              formik={props.formik}
              fullWidth
              label="Select SMS Sender"
              onChange={() => {
                props.formik.setFieldValue('smsBusinessNumber', null);
                props.formik.setFieldValue('smsInboxId', undefined);
              }}
              required
              size="small"
              variant="outlined"
            >
              {smsSender.map((smsSenderValue, index) => (
                <MenuItem key={index} value={smsSenderValue.value}>
                  {smsSenderValue.label}
                </MenuItem>
              ))}
            </FormikSelect>
          </div>
          {props.formik.values.smsSender === 'teamSMS' && (
            <div className="subField">
              <div className="col-lg-8">
                <h5 className="fieldTitle">SMS Inbox</h5>
                <p className="fieldDescription">
                  Select the Team SMS Inbox from which SMS messages will be sent
                  to the contact.
                </p>
              </div>
              <div className="col-lg-4 pl-0">
                <FormikSelect
                  name="smsInboxId"
                  // className={classes.field}
                  formik={props.formik}
                  fullWidth
                  label="Select SMS Inbox"
                  onChange={() =>
                    props.formik.setFieldValue('smsBusinessNumber', null)
                  }
                  required
                  size="small"
                  variant="outlined"
                >
                  {businessSMSArray
                    .filter((businessSMS) => businessSMS.active == 1)
                    .map((businessSMS, index) => (
                      <MenuItem key={index} value={businessSMS.businessSMSId}>
                        {businessSMS.name}
                      </MenuItem>
                    ))}
                </FormikSelect>
              </div>
            </div>
          )}
          {props.formik.values.smsSender === 'teamSMS' &&
            props.formik.values.smsInboxId && (
              <div className="subField">
                <div className="col-lg-8">
                  <h5 className="fieldTitle">SMS Business Number</h5>
                  <p className="fieldDescription">
                    Select the business number associated with the Team SMS
                    Inbox that will be used to send SMS messages. This will be
                    the number that appears to the contact receiving the
                    message.
                  </p>
                </div>
                <div className="col-lg-4 pl-0">
                  <FormikSelect
                    name="smsBusinessNumber"
                    // className={classes.field}
                    formik={props.formik}
                    fullWidth
                    label="Select SMS Business Number"
                    required
                    size="small"
                    variant="outlined"
                  >
                    {businessInboxNumbers.map((businessInboxNumber, index) => (
                      <MenuItem key={index} value={businessInboxNumber.DID}>
                        {businessInboxNumber.DID}
                      </MenuItem>
                    ))}
                  </FormikSelect>
                </div>
              </div>
            )}
        </div>
        <div className="row borderBottom">
          <div className="col-lg-8">
            <h5 className="fieldTitle">Allow Re-enrollment</h5>
            <p className="fieldDescription">
              Toggle to allow contacts to be re-enrolled into this Cadence via
              triggering the webhook again or as part of a Call Outcome
              (Disposition) rule.
            </p>
          </div>
          <div className="col-lg-4">
            <FormControlLabel
              control={
                <FormikSwitch
                  name="reEnrollmentEnabled"
                  formik={props.formik}
                  color="primary"
                  type="checkbox"
                />
              }
              label="Allow Re-enrollment"
            />
          </div>
        </div>
        <div className="row borderBottom">
          <div className="col-lg-8">
            <h5 className="fieldTitle">Check DNC List</h5>
            <p className="fieldDescription">
              Enable this option to automatically check if a contact is detected
              by Kixie's proprietary DNC algorithm as likely to be included on
              the Do Not Call Registry before enrolling them in the Cadence.
            </p>
          </div>
          <div className="col-lg-4">
            <FormControlLabel
              control={
                <FormikSwitch
                  name="checkDncEnabled"
                  formik={props.formik}
                  color="primary"
                  type="checkbox"
                />
              }
              label="Check DNC List"
            />
          </div>
        </div>
        <div className="row borderBottom pb-5">
          <div className="col-lg-8">
            <h5 className="fieldTitle">Cadence Hours</h5>
            <p className="fieldDescription">
              Specify the cadence hours, based on your{' '}
              <a href="/manage/account#settings" target="_blank">
                business timezone
              </a>
              , during which actions in this Cadence will be triggered. Actions
              will be paused outside these hours until the next available
              window.
            </p>
            Actions will be routed:
            {cadenceHoursString.map((string, index) => (
              <div key={index}>
                {string} <br />
              </div>
            ))}
          </div>
          <div className="col-lg-4">
            <FormControlLabel
              control={
                <FormikSwitch
                  name="useBusinessCadenceHoursEnabled"
                  formik={props.formik}
                  color="primary"
                  type="checkbox"
                />
              }
              label="Use Business Hours"
              className="my-4"
            />
            <Button
              variant="contained"
              color="primary"
              className={classes.editButton}
              onClick={handleShowBusinessHours}
              disabled={props.formik.values.useBusinessCadenceHoursEnabled}
            >
              Edit Cadence Hours
            </Button>
          </div>
        </div>
        <div className="row pb-5">
          <div className="col-lg-8">
            <h5 className="fieldTitle">Target Hours</h5>
            <p className="fieldDescription">
              Specify the target hours, based on the local timezone relative to
              the area code of the number enrolled in the cadence, when actions
              will be executed for optimal reach. Target hours that fall outside
              of cadence hours will not be respected.
            </p>
            Actions will be routed:
            {targetHoursString.map((string, index) => (
              <div key={index}>
                {string} <br />
              </div>
            ))}
          </div>
          <div className="col-lg-4">
            <FormControlLabel
              control={
                <FormikSwitch
                  name="useBusinessTargetHoursEnabled"
                  formik={props.formik}
                  color="primary"
                  type="checkbox"
                />
              }
              label="Use Business Hours"
              className="my-4"
            />
            <Button
              variant="contained"
              color="primary"
              className={classes.editButton}
              onClick={handleShowTargetHours}
              disabled={props.formik.values.useBusinessTargetHoursEnabled}
            >
              Edit Target Hours
            </Button>
          </div>
        </div>
        <ModalWithStyles
          onSubmit={handleSubmit}
          title={showBusinessHours ? 'Cadence Hours' : 'Target Hours'}
          show={showBusinessHours || showTargetHours}
          onHide={onHide}
          isSubmitting={props.formik.isSubmitting}
          size="lg"
        >
          <Scheduler
            //if showBusinessHours is true we set cadence hours , otherwise set the target hours
            schedulableHours={
              showBusinessHours
                ? props.formik.values.cadenceHours
                : props.formik.values.targetHours
            }
            setSchedulableHours={handleSetSchedulableHours}
          />
        </ModalWithStyles>
      </div>
    </>
  );
}
